import {useCallback, useEffect, useState, useMemo, useRef} from 'react'
import {Map, Source, Layer, NavigationControl, ScaleControl} from './MaptilerWrapper'
import {useConfig} from '../../../../store/hooks/config'
import FocusToState from './FocusToState'
import DashboardNoData from './DashboardNoData'
import ResetNationView from './ResetNationView'
import {
  unclusteredLayerHospitals,
  unclusteredLayerUrgentCare,
  unclusteredLayerGrocery,
  unclusteredLayerPharmacy,
  unclusteredLayerChildcare,
  unclusteredLayerObgyn,
  unclusteredLayerMentalHealthCareFacilities,
  unclusteredLayerFqhc
} from './Layers/config'
import {useGetPOI} from './PopulationDensityChartQueries/PointsOfInterestQuery'
import {
  convertPoiToGeoJSON,
  convertUniqueHouseholdsToGeoJSON,
  useUniqueAddressessCount
} from './PopulationDensityHelper'
import {useStatesAndHealthConditions} from '../../../../store/hooks/breadCrumbs'
import {useGetPopulationDensity} from './PopulationDensityChartQueries/PopulationDensityQuery'
import useCongressionalDistrict from './PopulationDensityChartQueries/CongressionalDistrictQuery'
import {usePointOfInterest} from '../../../../store/hooks/pointOfInterest'
import POILegendComponent from './Legends/POILegendComponent'
import ChartLoadingIndicator from '../../../common/ChartLoadingIndicator'
import StateTilesLayer from './Layers/StateTilesLayer'
import {usePopulationAggregation} from '../../../../store/hooks/populationAggregation'
import {getState} from '../../../../shared/helpers/getState'
import {
  statesToFIPS,
  stateFIPSBounds,
  loadImages,
  getMapAccessKey,
  renderClusterLayer,
  renderClusterCountLayer,
  handleCongressionalDistrictHover,
  getTableColumns,
  getTableDataToExport,
  checkIsFilterApplied,
  updateOutreachPercentageColumnLabels,
  getUpdatedData,
  handleCensusTractHover,
  getMissingCountyData,
  handleStateHover
} from './helpers'
import {useGeograhicBoundary} from '../../../../store/hooks/geographicBoundary'
import CountyTilesLayer from './Layers/CountyTilesLayer'
import {useGetUniqueHouseholds} from './PopulationDensityChartQueries/UniqueHouseholdsQuery'
import {BaseTooltipComponent} from '../../../common/BaseTooltipComponent'
import ChartDataRefetchOnError from '../../../common/ChartDataRefetchOnError'
import PopulationDensityDropDown from '../../cards/PopulationDensityDropDown'
import {useTableViewContext} from '../../../../store/hooks/tableView'
import TableVirtualized from '../../../TableVirtual/TableVirtualized'
import tableData from '../../../../appConfig/populationDensityTable.config.json'
import {CircularProgress} from '@mui/material'
import {useTheme} from '@emotion/react'
import {StyledDiv, MapDivStyles} from './Styles'
import PropTypes from 'prop-types'
import {useExport} from '../../../../store/hooks/export'
import VisualLayer from './Layers/VisualLayer'
import {useTotalUSPopulation} from '../../../../store/hooks/totalUSPopulation'
import {useOutreachFilters} from '../../../../store/hooks/useOutreachFilters'
import {getOutreachSimulationTooltipFieldData} from '../../helper'
import Personas from '../../persona/Personas'
import {
  usePersonaData,
  usePersonaByCongressionalDistrict,
  usePersonaByCensusTract
} from '../../../../store/hooks/personaMapData'
import {usePersona} from '../../../../store/hooks/persona'
import CongressionalDistrictLayer from './Layers/CongressionalDistrictLayer'
import CensusTractLayer from './Layers/CensusTractLayer'
import useCensusTractQuery from './PopulationDensityChartQueries/CensusTractQuery'
import useFilterManagement from '../../../../store/hooks/useFilterManagement'
import {useUniqueAddressesCount} from '../../../../store/hooks/uniqueAddressesCount'
import {useSelector} from 'react-redux'
import {selectCurrentUniqueAddressesCount} from '../../../../store/slices/uniqueAddressesCountSlice'

/*
  State,county and congressional district tileset source layers are kept here. These source layer names are visible in the maptiler cloud account,
  tileset section. Only to be changed when new tilesets are created. 
*/

const stateTilesetSourceLayer = 'states'
const countyTilesetSourceLayer = 'counties'
const congressionalDistrictTilesetSourceLayer = 'congressionaldistrict'
const censusTractTilesetSourceLayer = 'censustract'
const defaultMapLongitude = -95.7
const defaultMapLatitude = 42
const smallScreenLongitude = -90
const smallScreenLatitude = 38
const defaultZoomLevel = 2.5
const defaultMaxZoomLevel = 15.9
const largeScreenZoomLevel = 4
const smallScreenZoomLevel = 2.5
const mediumScreenZoomLevel = 3.5

// Helper function to add dominant persona to population table data
const addDominantPersonaToTableData = (
  tableData,
  selectedGeoBoundary,
  stateFipsToProperties,
  countyFipsToProperties,
  censusTractFipsToProperties
) => {
  return tableData.map((row) => {
    let dominantPersona = null
    if (selectedGeoBoundary === 'state') {
      dominantPersona = stateFipsToProperties?.[row.fips]?.dominant_persona || null
    } else if (selectedGeoBoundary === 'county') {
      dominantPersona = countyFipsToProperties?.[row.fips]?.dominant_persona || null
    } else if (selectedGeoBoundary === 'censustract') {
      dominantPersona = censusTractFipsToProperties?.[row.censusTractGeoId]?.dominant_persona || null
    }

    return {
      ...row,
      dominant_persona: dominantPersona
    }
  })
}

const PopulationDensityChart = ({width, height, isAdminPanelOpen, showChoropleth, setShowChoropleth, onMapReady}) => {
  const mapRef = useRef(null) // Create a ref to store the map instance
  const map = mapRef.current // Get the current map instance
  const isFirstLoad = useRef(true)
  const [mapBounds, setMapBounds] = useState({sw: {}, ne: {}, se: {}, nw: {}})
  const {poiData, loadingPOI, errorPointOfInterest, refetchPointOfInterest} = useGetPOI(mapBounds)
  const [poiGeojsonData, setPoiGeojsonData] = useState()
  const [hoverInfo, setHoverInfo] = useState()
  const [tileLayerHoverInfo, setTileLayerHoverInfo] = useState()
  const {filters, filterDetailsLoading} = useStatesAndHealthConditions()
  const {setExportTableData, setIsMapDataLoading} = useExport()
  const [populationAggregationData, setPopulationAggregationData] = useState()
  const [selectedLocalState, setSelectedLocalState] = useState([])
  const [uniqueHouseholdsData, setUniqueHouseholdsData] = useState([])
  const [uniqueAddressesGeojsonData, setUniqueAddressesGeojsonData] = useState()
  const {POIConfig: poiConfig, personasConfig} = useConfig() || {}
  const theme = useTheme()
  const [dataEmpty, setDataEmpty] = useState()
  const [populationTableData, setPopulationTableData] = useState([])
  const [countyDict, setCountyDict] = useState()
  const [congressionalDistrictDict, setCongressionalDistrictDict] = useState()
  const [censusTractDict, setCensusTractDict] = useState()
  const [stateDict, setStateDict] = useState()
  const [columns, setColumns] = useState(tableData.defaultColumns)
  const [isFrozen, setIsFrozen] = useState(false)
  const {showPopulationDensityTable, selectedLabel} = useTableViewContext()

  const {setCurrentUniqueAddressesCount: setUniqueAddressesCount} = useUniqueAddressesCount()
  const uniqueAddressesCount = useSelector(selectCurrentUniqueAddressesCount)
  // Memoize the updated data to prevent infinite re-renders
  const memoizedTableData = useMemo(
    () => getUpdatedData(populationTableData, personasConfig),
    [populationTableData, personasConfig]
  )

  const {
    countyFipsToPersona,
    countyFipsToProperties,
    stateFipsToPersona,
    stateFipsToProperties,
    loading: loadingPersona,
    error: errorPersona,
    refetch: refetchPersona
  } = usePersonaData()

  const {
    personaByCongressionalDistrict,
    congressionalDistrictFipsToPersona,
    congressionalDistrictFipsToProperties,
    loading: loadingPersonaByCongressionalDistrict,
    personaByCongressionalDistrictError,
    refetchPersonaByCongressionalDistrict
  } = usePersonaByCongressionalDistrict()

  // Census Tract Persona Data Fetch
  const {
    personaByCensusTract,
    loading: loadingPersonaByCensusTract,
    censusTractFipsToPersona,
    censusTractFipsToProperties,
    personaByCensusTractError,
    refetch: refetchPersonaByCensusTract
  } = usePersonaByCensusTract()

  const {personasToggle} = usePersona()

  const personaUnclusteredLayers =
    Object.keys(personasConfig?.personas || {}).map((key) => `unclustered-${personasConfig?.personas?.[key]}`) || []
  const interactiveLayerIds = [
    'unclustered-hospitals',
    'unclustered-urgentCare',
    'unclustered-groceryStores',
    'unclustered-pharmacies',
    'unclustered-uniqueHousehold',
    'unclustered-fullEnrolled',
    'unclustered-notEnrolled',
    'unclustered-partialEnrolled',
    'unclustered-childCare',
    'unclustered-obgynPhysicianFacilities',
    'unclustered-mentalHealthCareFacilities',
    'unclustered-fqhc',
    'state-fill',
    'counties-fill',
    'congressionaldistrict-fill',
    'censustract-fill',
    'censustract-fill1',
    'censustract-fill2',
    'censustract-fill3',
    'censustract-fill4',
    'censustract-filln',
    ...personaUnclusteredLayers
  ]
  const {selectedPOI} = usePointOfInterest()
  const {setPopulationAggregationState, setLoadingAggregationsState} = usePopulationAggregation()
  const uniqueAddressMaximum = useUniqueAddressessCount()
  const [conditionalDensityQuery, {data: populationDensityData, loading, error, refetch}] = useGetPopulationDensity()
  const [populationAggregation, setPopulationAggregation] = useState(populationDensityData)
  const [loadingAggregations, setLoadingAggregations] = useState(loading)
  const {uniqueHouseholds, loadingUniqueHouseholds, errorUniqueHouseholds, refetchUniqueHouseholds} =
    useGetUniqueHouseholds(uniqueAddressesCount, loading || loadingAggregations)
  const {congressionalDistrictData, loadingCongressionalDistrict, errorLoadingCongressionalDistrict} =
    useCongressionalDistrict()

  // census tract data fetch
  const {censusTractData, loadingCensusTract, errorLoadingCensusTract, refetchCensusTract} = useCensusTractQuery()

  const {loadingTotalPopulation} = useTotalUSPopulation()
  // New filter management system for outreach
  const {isOutreachActive, enrolledValue, selectedOptionsWithConfig} = useOutreachFilters()
  const currentStateFromContext = useMemo(() => {
    return filters && getState(filters)
  }, [filters])
  const [isDominantPersonaLoading, setIsDominantPersonaLoading] = useState(false)
  const [isTableDataLoading, setIsTableDataLoading] = useState(true)
  const selectedFips = []
  if (Array.isArray(currentStateFromContext)) {
    for (const cur of currentStateFromContext) {
      const val = statesToFIPS.find((value) => value.name === cur)
      selectedFips.push(val?.code)
    }
  }
  const isShowChoroplethAvailable = showChoropleth ? 'visible' : 'none'
  const loadPoILegend = selectedPOI.length > 0 ? <POILegendComponent /> : ''
  const showVisibilty = showPopulationDensityTable || isAdminPanelOpen ? 'none' : 'block'
  // filtering out the selections from the outreach simulation filter

  const {appliedFilters} = useFilterManagement()
  const isFilterApplied = checkIsFilterApplied(appliedFilters)
  useEffect(() => {
    setPopulationAggregation(populationDensityData)
    setLoadingAggregations(loading)
    setPopulationAggregationState(populationDensityData)
    setLoadingAggregationsState(loading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationDensityData, loading])

  // Updated to use new filter management data
  const getOutreachColumnsForTable = (selectedGeoBoundary) => {
    if (!selectedOptionsWithConfig?.length) return {}

    return selectedOptionsWithConfig.reduce((acc, option) => {
      let currentCol
      if (enrolledValue === 1) {
        currentCol = tableData.outreachSimulationColumns[option.label]?.[0]
      } else if (enrolledValue === 0) {
        currentCol = tableData.outreachSimulationColumns[option.label]?.[1]
      } else if (enrolledValue === 2) {
        const cols = tableData.outreachSimulationColumns[option.label]
        if (Array.isArray(cols)) {
          let obj = {}
          for (const i of cols) {
            obj = {...obj, ...i}
          }
          currentCol = obj
        } else {
          currentCol = cols
        }
      }
      currentCol = updateOutreachPercentageColumnLabels(currentCol, selectedGeoBoundary, option.label)
      return {...acc, ...currentCol}
    }, {})
  }
  // Sorting the congressional District array
  // to display it in alphabetical order in table view
  const sortedCongressionalDistricts = useMemo(() => {
    return (congressionalDistrictData?.congressionalDistrict?.congressionalDistrict || []).slice().sort((a, b) => {
      if (a.state < b.state) return -1
      if (a.state > b.state) return 1
      const getDistrictNumber = (district) => {
        const match = district.match(/\d+/)
        return match ? Number.parseInt(match[0], 10) : 0
      }
      return getDistrictNumber(a.congressional_district) - getDistrictNumber(b.congressional_district)
    })
  }, [congressionalDistrictData])
  // taking dominent persona from the personaByCongressionalDistrict
  // and adding it to the congressionalDistrictData
  // to display it in table view
  const enrichedCongressionalDistricts = useMemo(() => {
    if (!personasToggle) return sortedCongressionalDistricts
    const personaMap = {}
    for (const pd of personaByCongressionalDistrict?.personaCongressionalDistrict?.personaCongressionalDistrict || []) {
      personaMap[`${pd.state}_${pd.congressional_district_id}`] = pd.dominant_persona
    }
    return sortedCongressionalDistricts.map((d) => ({
      ...d,
      dominant_persona: personaMap[`${d.state}_${d.congressional_district_id}`] ?? null
    }))
  }, [sortedCongressionalDistricts, personaByCongressionalDistrict, personasToggle])

  const getBenefitsEligibilityCol = () => {
    const finalValue = getOutreachColumnsForTable(selectedGeoBoundary)
    // Check if the selectedGeoBoundary is 'congressionalDistrict'
    if (selectedGeoBoundary === 'congressionaldistrict') {
      return {
        State: 'state',
        'Congressional District': 'congressional_district',
        ...finalValue
      }
    }
    // Default return
    return {State: 'state', ...finalValue}
  }

  const mapAccessToken = getMapAccessKey()

  map?.on('load', () => {
    // Hide Country name
    // map?.setLayoutProperty('Country labels', 'visibility', 'none')
  })

  useEffect(() => {
    if (map) {
      map.resize() // Updated this if statement as for the notification banner closure it is resizing the map
    }
  }, [width, height, map])

  useMemo(() => {
    setUniqueHouseholdsData(
      !uniqueAddressesCount || uniqueAddressesCount > uniqueAddressMaximum ? {uniqueHouseholds: []} : uniqueHouseholds
    )
    // uniqueAddressesCount < uniqueAddressMaximum && updateTogglePopulationDensity(false)
  }, [uniqueAddressesCount, uniqueAddressMaximum, uniqueHouseholds])

  useEffect(() => {
    // Load the SVG image
    loadImages(map, poiConfig)
  }, [poiData, map, poiConfig])

  useEffect(() => {
    // if uniqueHouseholds data is loaded, then convert it to geojson format and
    // set the state variable uniqueAddressesGeojsonData to the current data
    const householdsData = uniqueHouseholds?.uniqueHouseholds
    householdsData
      ? setUniqueAddressesGeojsonData(convertUniqueHouseholdsToGeoJSON(householdsData))
      : setUniqueAddressesGeojsonData([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingUniqueHouseholds, uniqueHouseholdsData])

  useEffect(() => {
    setPopulationAggregationData(populationAggregation?.populationAggregation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAggregations])

  useEffect(() => {
    let totalUniqueAddresses = null
    if (populationAggregationData) {
      const uniqueAddresses =
        (populationAggregationData && populationAggregation?.populationAggregation.map(({count}) => count)) || []
      totalUniqueAddresses = uniqueAddresses.reduce((partialSum, currentSum) => partialSum + currentSum, 0)
      const stateDataByFips = {}
      // Creating Data Dictionary for state, to find data based on FIPS faster
      for (const obj of populationAggregation?.populationAggregation || []) {
        stateDataByFips[obj.fips] = {
          state: obj.state,
          population: obj.population,
          count: obj.count,
          statePopulation: obj.statePopulation,
          statePercentage: obj.statePercentage,
          usPercentage: obj.usPercentage,
          ...getOutreachSimulationTooltipFieldData(obj)
        }
      }

      setStateDict(stateDataByFips)
    }
    setUniqueAddressesCount(totalUniqueAddresses)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationAggregationData, loadingAggregations])

  const stateFilterKey = useMemo(() => {
    return currentStateFromContext ? currentStateFromContext.join(',') : ''
  }, [currentStateFromContext])

  useEffect(() => {
    setPoiGeojsonData([]) // Reset data first
    if (!loadingPOI && poiData?.placesOfInterest) {
      if (selectedPOI.length > 0) {
        const poiArray = []
        for (const [key, arr] of Object.entries(poiData.placesOfInterest)) {
          poiArray.push(convertPoiToGeoJSON(key, arr))
        }
        setPoiGeojsonData(poiArray)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPOI, selectedPOI, stateFilterKey, Object.keys(poiData?.placesOfInterest || {}).join(',')])

  const {selectedGeoBoundary} = useGeograhicBoundary()

  useEffect(() => {
    const matchQueryLargeScreen = globalThis.matchMedia('(min-width: 2560px)')
    const matchQuerySmallScreen = globalThis.matchMedia('(max-width:1366px)')

    if (matchQueryLargeScreen.matches) {
      map?.setZoom(largeScreenZoomLevel)
    } else if (matchQuerySmallScreen.matches) {
      map?.flyTo({
        center: [smallScreenLongitude, smallScreenLatitude],
        zoom: smallScreenZoomLevel
      })
    } else {
      map?.flyTo({
        center: [defaultMapLongitude, defaultMapLatitude],
        zoom: mediumScreenZoomLevel
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalThis.innerWidth])
  /* 
  This Function will highlight the hovered county and set Proper Information 
  on the TileLayer Tooltip 
  */
  const handleCountyHover = useCallback(
    (event) => {
      // Get the County Layer
      const countyFeatures = event.target.queryRenderedFeatures(event.point, {
        layers: map?.getLayer('counties-fill') && ['counties-fill']
      })

      /* Remove enabled feature state to remove highlight from the
       previously selected county */
      map?.removeFeatureState({
        source: 'counties',
        sourceLayer: countyTilesetSourceLayer
      })

      // If county layer is hovered, highlight the selected county
      if (countyFeatures[0] && map?.getLayer('counties-fill')) {
        map?.setFeatureState(
          {
            source: 'counties',
            sourceLayer: countyTilesetSourceLayer,
            id: countyFeatures[0].id
          },
          {
            hover: true
          }
        )

        // Set proper information on Tilelayer Tooltip
        setHoverInfo()
        let info = null
        if (populationDensityData?.missingGeoIds?.includes(countyFeatures[0]?.id)) {
          const missingData = getMissingCountyData(countyFeatures)

          info = {
            id: countyFeatures[0]?.layer?.id,
            data: missingData,
            personaData: personasToggle ? countyFipsToProperties[countyFeatures[0]?.id] : false,
            lng: event.lngLat.lng,
            lat: event.lngLat.lat,
            x: event?.point?.x,
            y: event?.point?.y
          }
        } else {
          info = {
            id: countyFeatures[0]?.layer?.id,
            data: countyDict[countyFeatures[0]?.id],
            personaData: personasToggle ? countyFipsToProperties[countyFeatures[0]?.id] : false,
            lng: event.lngLat.lng,
            lat: event.lngLat.lat,
            x: event?.point?.x,
            y: event?.point?.y
          }
        }

        countyDict && setTileLayerHoverInfo(info)
      }
    },
    [map, countyDict, personasToggle, countyFipsToProperties, populationDensityData]
  )

  const updateInteractiveLayers = (myInteractiveLayers) => {
    if (map?.getLayer('unclustered-uniqueHousehold')) myInteractiveLayers.push('unclustered-uniqueHousehold')
    if (map?.getLayer('unclustered-fullEnrolled')) myInteractiveLayers.push('unclustered-fullEnrolled')
    if (map?.getLayer('unclustered-notEnrolled')) myInteractiveLayers.push('unclustered-notEnrolled')
    if (map?.getLayer('unclustered-partialEnrolled')) myInteractiveLayers.push('unclustered-partialEnrolled')

    for (const layer of personaUnclusteredLayers) {
      if (map?.getLayer(layer)) myInteractiveLayers.push(layer)
    }
  }

  // handling tooltip freeze functionality
  const handleFreeze = () => {
    tileLayerHoverInfo !== null && setIsFrozen((currentIsFrozen) => !currentIsFrozen)
  }

  const handleHover = useCallback(
    (event) => {
      if (isFrozen) return // If the map is frozen, do not handle hover events
      const myInteractiveLayers = []
      let features = []
      // Loop through the location types array
      if (!loadingPOI && selectedPOI.length > 0) {
        for (const type of selectedPOI) {
          // Check if the type exists in the array of objects
          myInteractiveLayers.push(`unclustered-${type}`)
        }
        features = event?.target?.queryRenderedFeatures(event.point, {
          layers: myInteractiveLayers
        })
      }
      updateInteractiveLayers(myInteractiveLayers)
      features = event?.target?.queryRenderedFeatures(event.point, {
        layers: myInteractiveLayers
      })
      if (features[0] && features[0]?.properties?.cluster !== true) {
        setTileLayerHoverInfo()
        const info = {
          id: features[0]?.layer.id,
          data: features[0]?.properties,
          lng: event.lngLat.lng,
          lat: event.lngLat.lat,
          x: event?.point?.x,
          y: event?.point?.y
        }
        setHoverInfo(info)
      } else {
        // Based on the View call the hover method
        selectedGeoBoundary === 'state' &&
          handleStateHover(event, map, stateFipsToProperties, personasToggle, setTileLayerHoverInfo, {
            stateTilesetSourceLayer,
            stateDict,

            setHoverInfo,
            populationAggregationData
          })
        selectedGeoBoundary === 'county' && handleCountyHover(event)
        selectedGeoBoundary === 'censustract' &&
          handleCensusTractHover(
            event,
            map,
            censusTractDict,
            personasToggle,
            censusTractFipsToProperties,
            setTileLayerHoverInfo,
            {censusTractTilesetSourceLayer, censusTractData}
          )
        selectedGeoBoundary === 'congressionaldistrict' &&
          handleCongressionalDistrictHover(
            event,
            map,
            congressionalDistrictDict,
            personasToggle,
            congressionalDistrictFipsToProperties,
            setTileLayerHoverInfo,
            {congressionalDistrictTilesetSourceLayer, congressionalDistrictData}
          )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loadingPOI,
      selectedPOI,
      selectedGeoBoundary,
      handleStateHover,
      handleCountyHover,
      congressionalDistrictDict,
      congressionalDistrictFipsToProperties,
      censusTractDict,
      censusTractFipsToProperties,
      stateDict,
      map,
      personasToggle,
      updateInteractiveLayers,
      isFrozen
    ]
  )

  const handleLeave = useCallback(() => {
    // This is the correct check. If the tooltip is frozen, do nothing.
    if (isFrozen) return

    // If not frozen, proceed to clear the hover effects and tooltip info.
    map?.removeFeatureState({source: 'states', sourceLayer: stateTilesetSourceLayer})
    map?.removeFeatureState({source: 'counties', sourceLayer: countyTilesetSourceLayer})
    const censusTractSources = [
      'censustract',
      'censustract1',
      'censustract2',
      'censustract3',
      'censustract4',
      'censustractn'
    ]
    for (const source of censusTractSources) {
      if (map?.getSource(source)) {
        map?.removeFeatureState({source, sourceLayer: censusTractTilesetSourceLayer})
      }
    }
    map?.removeFeatureState({source: 'congressionaldistrict', sourceLayer: congressionalDistrictTilesetSourceLayer})

    setHoverInfo(null)
    setTileLayerHoverInfo(null)
  }, [isFrozen, map])

  // Removed useEffect with [mapRef.current] - bounds are now updated in onload and onMoveEnd handlers

  // If populationDensityData is there, create the data dictionary to be used later
  useEffect(() => {
    if (!loading) {
      const countyData = populationDensityData?.populationDensity
      const countyDataByFips = {}
      if (countyData && countyData.length > 0) {
        for (const data of countyData) {
          countyDataByFips[data.fips] = {
            state: data.state,
            county: data.county,
            population: data.population,
            count: data.count,
            countyPopulation: data.countyPopulation,
            countyPercentage: data.countyPercentage,
            statePercentage: data.statePercentage,
            ...getOutreachSimulationTooltipFieldData(data)
          }
        }
      }
      setCountyDict(countyDataByFips)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [populationDensityData])

  // congressional district data here
  useEffect(() => {
    if (!loadingCongressionalDistrict) {
      const cdData = congressionalDistrictData?.congressionalDistrict?.congressionalDistrict
      if (cdData && cdData.length > 0) {
        const congressionalDistrictDataByFips = {}
        for (const data of cdData) {
          congressionalDistrictDataByFips[data.congressional_district_id] = {
            state: data.state,
            congressional_district: data.congressional_district,
            population: data.population,
            count: data.count,
            congressionalDistrictPopulation: data.congressionalDistrictPopulation,
            congressionalDistrictPercentage: data.congressionalDistrictPercentage,
            statePercentage: data.statePercentage,
            ...getOutreachSimulationTooltipFieldData(data)
          }
        }
        setCongressionalDistrictDict(congressionalDistrictDataByFips)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCongressionalDistrict, errorLoadingCongressionalDistrict])

  // census tract data here
  useEffect(() => {
    if (!loadingCensusTract) {
      const ctData = censusTractData?.censusTract?.censusTract
      if (ctData && ctData.length > 0) {
        const censusTractDataByGeoId = {}
        for (const data of ctData) {
          censusTractDataByGeoId[data.censusTractGeoId] = {
            state: data.state,
            population: data.population,
            count: data.count,
            county: data.county,
            censusTractPopulation: data.censusTractPopulation,
            censusTractPercentage: data.censusTractPercentage,
            statePercentage: data.statePercentage,
            countyPercentage: data.countyPercentage,
            ...getOutreachSimulationTooltipFieldData(data)
          }
        }
        // Setting Census Tract Hover Data here...
        setCensusTractDict(censusTractDataByGeoId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCensusTract, errorLoadingCensusTract])

  const isPersonaDataReadyForBoundary = (boundary) => {
    if (boundary === 'state') return Boolean(stateFipsToProperties)
    if (boundary === 'county') return Boolean(countyFipsToProperties)
    if (boundary === 'censustract') return Boolean(censusTractFipsToProperties)
    return false
  }

  // To Display Leading Personas
  useEffect(() => {
    if (
      personasToggle &&
      populationTableData.length > 0 &&
      selectedGeoBoundary &&
      isPersonaDataReadyForBoundary(selectedGeoBoundary)
    ) {
      // Check if dominant_persona is already added to avoid unnecessary updates
      const isDominantPersonaAdded = populationTableData.every((row) => row.dominant_persona !== undefined)
      if (!isDominantPersonaAdded) {
        setIsDominantPersonaLoading(true)
        // Mapping of dominant_persona to populationTableData based on fips
        const updatedPopulationTableData = addDominantPersonaToTableData(
          populationTableData,
          selectedGeoBoundary,
          stateFipsToProperties,
          countyFipsToProperties,
          censusTractFipsToProperties
        )

        setPopulationTableData(updatedPopulationTableData) // Update the state with the new data
        setIsDominantPersonaLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    personasToggle,
    selectedGeoBoundary,
    stateFipsToProperties,
    countyFipsToProperties,
    censusTractFipsToProperties,
    populationTableData,
    congressionalDistrictFipsToProperties,
    isOutreachActive
  ])

  // To Download Excel sheet View
  useEffect(() => {
    if (
      personasToggle &&
      populationTableData?.length > 0 &&
      selectedGeoBoundary &&
      isPersonaDataReadyForBoundary(selectedGeoBoundary)
    ) {
      // Check if dominant_persona is already added to avoid unnecessary updates
      const isDominantPersonaAdded = populationTableData.every((row) => row.dominant_persona !== undefined)
      if (!isDominantPersonaAdded) {
        setIsDominantPersonaLoading(true)
        // Mapping of dominant_persona to populationTableData based on fips
        const updatedPopulationTableData = addDominantPersonaToTableData(
          populationTableData,
          selectedGeoBoundary,
          stateFipsToProperties,
          countyFipsToProperties,
          censusTractFipsToProperties
        )

        setPopulationTableData(updatedPopulationTableData) // Update the state with the new data
        setIsDominantPersonaLoading(false)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    personasToggle,
    selectedGeoBoundary,
    stateFipsToProperties,
    countyFipsToProperties,
    congressionalDistrictFipsToProperties,
    censusTractFipsToProperties,
    isOutreachActive
  ])

  // To Download Excel sheet View
  useEffect(() => {
    setIsTableDataLoading(true)
    // Wait for required data to be loaded before proceeding
    if (
      (selectedGeoBoundary === 'congressionaldistrict' &&
        personasToggle &&
        (loadingPersonaByCongressionalDistrict ||
          !Array.isArray(
            personaByCongressionalDistrict?.personaCongressionalDistrict?.personaCongressionalDistrict
          ))) ||
      (selectedGeoBoundary === 'congressionaldistrict' &&
        !personasToggle &&
        !Array.isArray(congressionalDistrictData?.congressionalDistrict?.congressionalDistrict)) ||
      (selectedGeoBoundary === 'censustract' &&
        personasToggle &&
        (loadingPersonaByCensusTract ||
          !Array.isArray(personaByCensusTract?.personaCensusTract?.personaCensusTract))) ||
      (selectedGeoBoundary === 'censustract' &&
        !personasToggle &&
        !Array.isArray(censusTractData?.censusTract?.censusTract)) ||
      (selectedGeoBoundary === 'county' && !Array.isArray(populationDensityData?.populationDensity)) ||
      (selectedGeoBoundary === 'state' && !Array.isArray(populationAggregationData))
    ) {
      // Data not ready yet, don't update state
      return
    }

    const getDominantPersonaData = (data, boundary) => {
      return addDominantPersonaToTableData(
        Array.isArray(data) ? data : [],
        boundary,
        stateFipsToProperties,
        countyFipsToProperties,
        censusTractFipsToProperties
      )
    }

    const tableDataToExport = getTableDataToExport({
      selectedGeoBoundary,
      personasToggle,
      enrichedCongressionalDistricts,
      sortedCongressionalDistricts,
      populationDensityData,
      censusTractData,
      getDominantPersonaData,
      populationAggregationData
    })

    if (selectedGeoBoundary === 'congressionaldistrict' && personasToggle) {
      setPopulationTableData(enrichedCongressionalDistricts)
    } else if (selectedGeoBoundary === 'congressionaldistrict') {
      setPopulationTableData(sortedCongressionalDistricts || [])
    } else {
      setPopulationTableData(tableDataToExport)
    }

    const columns = getTableColumns(
      isOutreachActive,
      selectedGeoBoundary,
      personasToggle,
      tableData,
      getBenefitsEligibilityCol,
      {isFilterApplied}
    )
    setExportTableData({
      tableRows: tableDataToExport,
      tableColNames: columns
    })
    setIsTableDataLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showPopulationDensityTable,
    selectedGeoBoundary,
    populationDensityData,
    censusTractData,
    populationAggregationData,
    personasToggle,
    stateFipsToProperties,
    countyFipsToProperties,
    censusTractFipsToProperties,
    isOutreachActive,
    selectedOptionsWithConfig,
    enrolledValue,
    personaByCongressionalDistrict,
    personaByCensusTract,
    congressionalDistrictData,
    loadingPersonaByCensusTract,
    loadingPersonaByCongressionalDistrict
  ])

  // This function calculates the center of given box bounds
  const calculateCenter = (bounds) => {
    const longitudeSum = (bounds[0][0] + bounds[1][0]) / 2
    const latitudeSum = (bounds[0][1] + bounds[1][1]) / 2
    return [longitudeSum, latitudeSum]
  }

  // The following hook is responsible for the fly to feature. When data is loaded and one state is selected,
  // the map flies to the center of the bounds of the selected state. The bounds are defined in helper.
  // Helpers to reduce cognitive complexity of the selection effect
  const resetToNationalView = () => {
    if (selectedLocalState.length) setSelectedLocalState([])
    map?.flyTo({
      center: [defaultMapLongitude, defaultMapLatitude],
      zoom: defaultZoomLevel
    })
  }

  const flyToSingleState = (stateName) => {
    if (!stateName || stateName === 'No Data') return
    setSelectedLocalState(stateName)
    const fips = statesToFIPS.find((v) => v.name === stateName)
    if (!fips) return
    const bounds = stateFIPSBounds[fips.code]
    if (!bounds) return
    map?.flyTo({
      center: calculateCenter(bounds),
      zoom: fips.code === '11' ? 10 : 4.5
    })
  }

  const flyOrResetForTwoStates = (stateNames) => {
    if (selectedLocalState.length) setSelectedLocalState([])
    const codes = stateNames.map((n) => statesToFIPS.find((v) => v.name === n)?.code).filter(Boolean)
    if (codes.includes('02')) {
      // Alaska in the pair → show initial national view
      resetToNationalView()
      return
    }
    // Union bounds
    let minLng = Infinity
    let minLat = Infinity
    let maxLng = -Infinity
    let maxLat = -Infinity
    for (const code of codes) {
      const b = stateFIPSBounds[code]
      if (!b) continue
      const [[lng1, lat1], [lng2, lat2]] = b
      minLng = Math.min(minLng, lng1, lng2)
      minLat = Math.min(minLat, lat1, lat2)
      maxLng = Math.max(maxLng, lng1, lng2)
      maxLat = Math.max(maxLat, lat1, lat2)
    }
    map?.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat]
      ],
      {
        padding: {top: 40, right: 40, bottom: 40, left: 40},
        maxZoom: 6,
        animate: true
      }
    )
  }

  const applySelectionFocus = () => {
    const names = Array.isArray(currentStateFromContext)
      ? currentStateFromContext.filter((n) => n && n !== 'No Data')
      : []

    if (names.length === 0) return

    if (names.length === 1) {
      flyToSingleState(names[0])
      return
    }

    if (names.length === 2) {
      flyOrResetForTwoStates(names)
      return
    }
    if (names.length > 2 && selectedLocalState.length) {
      resetToNationalView()
    }
  }
  useEffect(() => {
    applySelectionFocus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStateFromContext, selectedLocalState, map])

  // Count how many valid states are selected (excluding 'No Data')
  const selectedStatesCount = useMemo(() => {
    const names = Array.isArray(currentStateFromContext)
      ? currentStateFromContext.filter((n) => n && n !== 'No Data')
      : []
    return names.length
  }, [currentStateFromContext])

  useEffect(() => {
    if (
      loading ||
      loadingAggregations ||
      loadingPersona ||
      loadingPersonaByCongressionalDistrict ||
      loadingPersonaByCensusTract
    )
      return
    if (isFirstLoad.current) {
      isFirstLoad.current = false
      return
    }
    setDataEmpty(checkForEmptyData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadingAggregations, loadingPersona, loadingPersonaByCongressionalDistrict, loadingPersonaByCensusTract])

  // Checks if Some Data is  Empty

  const checkForEmptyData = () => {
    const isPopulationAggregationEmpty =
      !populationAggregation?.populationAggregation || populationAggregation.populationAggregation.length === 0
    const isPopulationDensityEmpty =
      !populationDensityData?.populationDensity || populationDensityData.populationDensity.length === 0
    return isPopulationAggregationEmpty || isPopulationDensityEmpty
  }

  useEffect(() => {
    if (!filterDetailsLoading && currentStateFromContext?.length > 0 && !currentStateFromContext?.includes('No Data')) {
      conditionalDensityQuery()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDetailsLoading, stateFilterKey])

  const onload = useCallback(() => {
    const currentMap = mapRef.current
    currentMap?.setPaintProperty('Country labels', 'text-color', theme.palette.hpAccentBlack)
    currentMap?.setPaintProperty('State labels', 'text-color', theme.palette.hpAccentBlack)

    // Set initial map bounds when map loads
    if (currentMap) {
      const bounds = currentMap.getBounds()
      const {_sw, _ne} = bounds
      const nw = {lng: _sw.lng, lat: _ne.lat}
      const se = {lng: _ne.lng, lat: _sw.lat}
      setMapBounds({sw: _sw, ne: _ne, nw, se})

      // Call onMapReady callback if provided
      if (onMapReady) {
        onMapReady(currentMap)
      }
    }
  }, [theme.palette.hpAccentBlack, onMapReady])

  const handleMoveEnd = useCallback(() => {
    // Update map bounds when map is moved/zoomed
    const currentMap = mapRef.current
    if (currentMap) {
      const bounds = currentMap.getBounds()
      const {_sw, _ne} = bounds
      const nw = {lng: _sw.lng, lat: _ne.lat}
      const se = {lng: _ne.lng, lat: _sw.lat}
      setMapBounds({sw: _sw, ne: _ne, nw, se})
    }
  }, [])

  const renderUnclusterLayer = (type) => {
    switch (type) {
      case 'hospitals':
        return (
          <Layer
            {...unclusteredLayerHospitals}
            layout={{'icon-image': 'hospitals-svg', 'icon-allow-overlap': true, 'icon-size': 0.15}}
          />
        )
      case 'urgentCare':
        return (
          <Layer
            {...unclusteredLayerUrgentCare}
            layout={{'icon-image': 'urgentCare-svg', 'icon-allow-overlap': true, 'icon-size': 0.15}}
          />
        )
      case 'groceryStores':
        return (
          <Layer
            {...unclusteredLayerGrocery}
            layout={{'icon-image': 'groceryStores-svg', 'icon-allow-overlap': true, 'icon-size': 0.15}}
          />
        )
      case 'pharmacies':
        return (
          <Layer
            {...unclusteredLayerPharmacy}
            layout={{'icon-image': 'pharmacies-svg', 'icon-allow-overlap': true, 'icon-size': 0.15}}
          />
        )
      case 'childCare':
        return (
          <Layer
            {...unclusteredLayerChildcare}
            layout={{'icon-image': 'childcare-svg', 'icon-allow-overlap': true, 'icon-size': 0.15}}
          />
        )
      case 'obgynPhysicianFacilities':
        return (
          <Layer
            {...unclusteredLayerObgyn}
            layout={{'icon-image': 'obgynPhysicianFacilities-svg', 'icon-allow-overlap': true, 'icon-size': 0.15}}
          />
        )
      case 'mentalHealthCareFacilities':
        return (
          <Layer
            {...unclusteredLayerMentalHealthCareFacilities}
            layout={{'icon-image': 'mentalHealthCareFacilities-svg', 'icon-allow-overlap': true, 'icon-size': 0.15}}
          />
        )
      case 'fqhc':
        return (
          <Layer
            {...unclusteredLayerFqhc}
            layout={{'icon-image': 'fqhc-svg', 'icon-allow-overlap': true, 'icon-size': 0.15}}
          />
        )
      default:
        return null
    }
  }
  const dataLoading =
    loading ||
    loadingUniqueHouseholds ||
    loadingPOI ||
    loadingPersona ||
    loadingPersonaByCongressionalDistrict ||
    loadingPersonaByCensusTract ||
    loadingAggregations ||
    loadingCongressionalDistrict

  useEffect(() => {
    setColumns(tableData.defaultColumns)
  }, [])

  useEffect(() => {
    if (
      loading ||
      loadingUniqueHouseholds ||
      loadingTotalPopulation ||
      loadingAggregations ||
      loadingPOI ||
      dataEmpty ||
      loadingPersona ||
      loadingPersonaByCongressionalDistrict ||
      loadingPersonaByCensusTract ||
      loadingCongressionalDistrict ||
      loadingCensusTract
    )
      setIsMapDataLoading(true)
    else setIsMapDataLoading(false)
  }, [
    loading,
    loadingUniqueHouseholds,
    loadingTotalPopulation,
    loadingPOI,
    dataEmpty,
    loadingPersona,
    loadingPersonaByCongressionalDistrict,
    loadingPersonaByCensusTract,
    loadingAggregations,
    loadingCensusTract,
    loadingCongressionalDistrict,
    setIsMapDataLoading
  ])

  useEffect(() => {
    const columns = getTableColumns(
      isOutreachActive,
      selectedGeoBoundary,
      personasToggle,
      tableData,
      getBenefitsEligibilityCol,
      {isFilterApplied, dataKeyIdentifier: 'dataKey'}
    )
    setColumns(columns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGeoBoundary, isOutreachActive, selectedOptionsWithConfig, enrolledValue, personasToggle, isFilterApplied])

  const hasError = () => {
    return (
      !dataLoading &&
      (error ||
        errorUniqueHouseholds ||
        errorPointOfInterest ||
        errorPersona ||
        personaByCongressionalDistrictError ||
        (selectedGeoBoundary === 'censustract' && (personaByCensusTractError || errorLoadingCensusTract)))
    )
  }

  if (hasError()) {
    return (
      <>
        {personasToggle && <Personas />}
        <ChartDataRefetchOnError
          onTryAgain={(event) => {
            event.preventDefault()
            if (error) {
              refetch()
            }
            if (errorUniqueHouseholds) {
              refetchUniqueHouseholds()
            }
            if (errorPointOfInterest) {
              refetchPointOfInterest()
            }
            if (errorPersona) {
              refetchPersona()
            }
            if (personaByCongressionalDistrictError) {
              refetchPersonaByCongressionalDistrict()
            }
            if (personaByCensusTractError) {
              refetchPersonaByCensusTract()
            }
            if (errorLoadingCensusTract) {
              refetchCensusTract()
            }
          }}
        />
      </>
    )
  } else if (
    !loadingPersona &&
    !loading &&
    !loadingPersonaByCongressionalDistrict &&
    !loadingPersonaByCensusTract &&
    dataEmpty
  ) {
    return (
      <>
        {personasToggle && <Personas />}
        <DashboardNoData width={width} height={height} />
      </>
    )
  } else {
    return (
      <div>
        <MapDivStyles style={{width: 'auto'}} applyZIndex={true}>
          <PopulationDensityDropDown
            showChoropleth={showChoropleth}
            setShowChoropleth={setShowChoropleth}
            dataLoading={dataLoading}
          />
        </MapDivStyles>
        {personasToggle && <Personas />}
        {showPopulationDensityTable && (
          <>
            {dataLoading ||
            isDominantPersonaLoading ||
            isTableDataLoading ||
            !columns?.length ||
            !populationTableData?.length ? (
              <StyledDiv sx={{height}}>
                <CircularProgress />
              </StyledDiv>
            ) : (
              <TableVirtualized
                enablePadding={true}
                height={height}
                width={width}
                data={memoizedTableData}
                columns={columns}
                poiData={poiData}
                columnData={selectedLabel}
                selectedGeoBoundary={selectedGeoBoundary}
                chartType='density'
                isFilterApplied={isFilterApplied}
              />
            )}
          </>
        )}
        <Map
          ref={mapRef}
          apiKey={mapAccessToken}
          center={[defaultMapLongitude, defaultMapLatitude]}
          zoom={defaultZoomLevel}
          minZoom={defaultZoomLevel}
          maxZoom={defaultMaxZoomLevel}
          containerStyle={{
            width,
            height,
            display: showVisibilty,
            borderRadius: 8
          }}
          style={`https://api.maptiler.com/maps/0195cc95-c491-73db-a7f6-409d6087449a/style.json`}
          onMouseMove={handleHover}
          onMouseLeave={handleLeave}
          onMouseOut={handleLeave}
          onClick={handleFreeze}
          interactiveLayerIds={interactiveLayerIds}
          attributionControl={{compact: true}}
          navigationControl={false}
          geolocateControl={false}
          id='mapElement'
          onLoad={onload}
          onMoveEnd={handleMoveEnd}
        >
          {dataLoading && <ChartLoadingIndicator ismapbox={'true'} />}
          {/* This Div is used for zoom in/out and reset map buttons */}

          <NavigationControl showCompass={false} position={'bottom-left'} />
          {selectedStatesCount > 0 && selectedStatesCount <= 2 ? (
            <FocusToState onClick={applySelectionFocus} />
          ) : (
            <ResetNationView mapRef={map} />
          )}

          <MapDivStyles style={{width: 'auto', bottom: 0, right: 0, zIndex: 0}}>{loadPoILegend} </MapDivStyles>
          {!loadingPOI &&
            poiGeojsonData &&
            poiGeojsonData.length > 0 &&
            poiGeojsonData.map(([type, poi]) => (
              <Source
                key={`${type}-cluster`}
                id={type}
                type='geojson'
                data={poi}
                cluster={true}
                clusterMaxZoom={22}
                clusterRadius={40}
                clusterMinPoints={10}
              >
                {renderClusterLayer(type)}
                {renderClusterCountLayer(type)}
                {renderUnclusterLayer(type)}
              </Source>
            ))}

          {tileLayerHoverInfo && <BaseTooltipComponent mapRef={map} hoverInfo={tileLayerHoverInfo} />}

          <StateTilesLayer
            loadingAggregations={loadingAggregations}
            populationAggregationData={populationAggregationData}
            selectedFips={selectedFips}
            boundary={selectedGeoBoundary}
            stateTilesetSourceLayer={stateTilesetSourceLayer}
            visibility={isShowChoroplethAvailable}
            stateFipsToPersona={stateFipsToPersona}
            personasToggle={personasToggle}
          />

          {selectedGeoBoundary === 'county' && (
            <CountyTilesLayer
              populationDensityData={populationDensityData}
              countyTilesetSourceLayer={countyTilesetSourceLayer}
              visibility={isShowChoroplethAvailable}
              countyFipsToPersona={countyFipsToPersona}
              personasToggle={personasToggle}
            />
          )}

          {selectedGeoBoundary === 'congressionaldistrict' && (
            <CongressionalDistrictLayer
              congressionalDistrictData={congressionalDistrictData}
              congressionalDistrictTilesetSourceLayer={congressionalDistrictTilesetSourceLayer}
              visibility={isShowChoroplethAvailable}
              congressionalDistrictFipsToPersona={congressionalDistrictFipsToPersona}
              personasToggle={personasToggle}
            />
          )}

          {selectedGeoBoundary === 'censustract' && (
            <CensusTractLayer
              censusTractData={censusTractData}
              censusTractTilesetSourceLayer={censusTractTilesetSourceLayer}
              visibility={isShowChoroplethAvailable}
              censusTractFipsToPersona={censusTractFipsToPersona}
              personasToggle={personasToggle}
            />
          )}

          {!loadingUniqueHouseholds && uniqueAddressesGeojsonData?.length > 0 && (
            <VisualLayer data={uniqueAddressesGeojsonData[0]} />
          )}
          {hoverInfo && (
            <div className='map-tooltip'>
              <BaseTooltipComponent hoverInfo={hoverInfo} loadingUniqueHouseholds={loadingUniqueHouseholds} />
            </div>
          )}

          <ScaleControl position='bottom-left' unit='imperial' />
          {/* used to show the range of zoom in/out functionality, hence commenting it for future use  */}
        </Map>
      </div>
    )
  }
}

PopulationDensityChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  isAdminPanelOpen: PropTypes.bool,
  showChoropleth: PropTypes.bool,
  setShowChoropleth: PropTypes.func,
  onMapReady: PropTypes.func
}

export default PopulationDensityChart

/**
 * List of functions and their purpose:
 *
 * - addDominantPersonaToTableData: Adds dominant persona information to population table data based on FIPS.
 * - checkOutreachSimulationFilter: Filters and processes selected outreach simulation options for table data.
 * - getBenefitsEligibilityCol: Retrieves column configurations for benefits eligibility based on selected filters.
 * - calculateCenter: Calculates the geographic center of given map bounds.
 * - handleCountyHover: Highlights a hovered county and updates the tooltip with relevant information.
 * - handleStateHover: Highlights a hovered state and updates the tooltip with relevant information.
 * - handleHover: Handles hover events for interactive layers and updates tooltips accordingly.
 * - handleLeave: Resets hover states and clears tooltips when the mouse leaves the map.
 * - renderClusterLayer: Renders cluster layers for points of interest based on their type.
 * - renderClusterCountLayer: Renders cluster count layers for points of interest based on their type.
 * - renderUnclusterLayer: Renders unclustered layers for points of interest based on their type.
 * - hasError: Checks if there are any errors in loading data or queries.
 * - checkForEmptyData: Checks if population aggregation or density data is empty.
 * - getTableDataToExport: Prepares table data for export based on selected filters and personas.
 * - getColumns: Configures table columns dynamically based on selected filters and personas.
 * - onload: Customizes map properties like text color and visibility on load.
 */
